import {
    Control,
    ControlOptions,
    StudentOrTeacher,
    SendMessageParams,
    SendMessageType
} from '@/components/C1Opener/OpenerTypes';
import { ButtonVariant } from "@/components/CustomLinkButton";
import { MessagingChannel } from 'jschannel';

export enum C1View {
    LPV4 = 'lpv4',
    Assignment = 'assignment',
    CreateAssignment = 'create-assignment',
    AccessRule = 'access-rule',
}

export type InitData = {
    areAttemptsExhausted: boolean,
    id: string, // "activity1"
    c1View: C1View, // view in which the activity is launched
    isLastActivity?: boolean, // true if this is the last activity in the assignment
    context?: string, // activity context for attempt tracking
    lang: string, // "en" or "es"
    segment: string, // "<string, 'adult', 'secondary', OR 'primary'>",
    userId: string, // "gigya-user1"
    userRole: StudentOrTeacher, // "student/teacher"
    loWithoutControls?: boolean, // if true, the content does not have control buttons
    c1HelpUrl?: string, // The url for C1 help page
    userFirstName?: string, // student's first name
    analytics?: {
        completionCount: number, // count of SComp conversations completed
        timespent: number // time spent practising across all SComp activities (in milliseconds)
    }
}

export function init(channel: MessagingChannel): Promise<InitData> {
    return sendMessageToContainer(channel, 'init');
}

export function ready(channel: MessagingChannel): Promise<unknown> {
    return sendMessageToContainer(channel, 'ready');
}

export function fullScreen(channel: MessagingChannel, isFullScreen: boolean): Promise<unknown> {
    return sendMessageToContainer(channel, 'fullScreen', isFullScreen)
}

export function showNextActivityBtn(channel: MessagingChannel, isShowButton: boolean): Promise<unknown> {
    return sendMessageToContainer(channel, 'showNextActivityBtn', isShowButton);
}

export function sendTermination(channel: MessagingChannel, launchNextItem: boolean): Promise<unknown> {
    return sendMessageToContainer(channel, 'terminated', { launchNextItem });
}

/**
 * Show the exit confirmation modal in the container.
 * @param channel The JS channel to use
 * @param options The options for the modal.
 *  - isVisible: Whether the modal should be visible.
 */
export function showExitConfirmationModal(channel: MessagingChannel, options: { isVisible: boolean }): Promise<unknown> {
    return sendMessageToContainer(channel, 'showExitConfirmationModal', options)
}

/**
 * Sends an iframe event to the container.
 * @param channel The JS channel to use
 * @param eventType The type of event ('unload', 'beforeunload', 'click', 'touchstart')
 * @param eventData The event data to send
 * @returns A promise that is resolved by the channel
 */
export function sendIframeEvent(channel: MessagingChannel, eventType: string, eventData: Event): Promise<unknown> {
    return sendMessageToContainer(channel, 'iframeEvents', {
        eventType,
        eventData
    });
}

/**
 * Sends a message to the container to configure a control button.
 * @param channel The JS channel to use
 * @param controlButton The control button to configure
 * @param options - The configuration options object
 * @returns A promise that is resolved by the channel
 */
export function setControlButton(channel: MessagingChannel, controlButton: Control, options: ControlOptions): Promise<unknown> {
    return sendMessageToContainer(channel, 'controlsChange', {
        control: controlButton,
        meta: {
            type: 'button',
            variant: options.variant ?? ButtonVariant.Primary,
            ...(options.variant ? { variant: options.variant } : {})
        },
        visible: options.isVisible,
        active: options.isActive ?? true
    });
}

export function sendMessageToContainer<T>(
    channel: MessagingChannel,
    type: SendMessageType,
    data?: T
): Promise<T> {
    return new Promise((resolve, reject) => {
        channel.call({
            method: 'sendMessageToContainer',
            timeout: 5000,
            params: {
                type: type,
                data: data
            } as SendMessageParams,
            success: result => resolve(result),
            error: error => reject(error as Error)
        });
    });
}